import React from 'react';
import { styled } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Colors } from 'library';

type IconSize = 'xs' | 'sm' | 'md' | 'lg';
type IconTone = 'default' | 'muted' | 'outlined' | 'accent';

interface IconProps {
    icon: SvgIconComponent | IconDefinition;
    size?: IconSize;
    tone?: IconTone;
    iconColor?: string;
    bgColor?: string;
    className?: string;
}

const CIRCLE_PX: Record<IconSize, number> = { xs: 24, sm: 32, md: 44, lg: 72 };
const ICON_PX: Record<IconSize, number> = { xs: 12, sm: 16, md: 22, lg: 32 };

const TONE_BG: Record<IconTone, string> = {
    default: Colors.border,
    muted: Colors.surface,
    outlined: Colors.white,
    accent: Colors.secondarySoft,
};

const TONE_BORDER: Record<IconTone, string | undefined> = {
    default: undefined,
    muted: `1px solid ${Colors.border}`,
    outlined: `1px solid ${Colors.border}`,
    accent: undefined,
};

const TONE_ICON_COLOR: Record<IconTone, string> = {
    default: Colors.textSecondary,
    muted: Colors.textSecondary,
    outlined: Colors.textSecondary,
    accent: Colors.secondary,
};

function isFaIcon(icon: SvgIconComponent | IconDefinition): icon is IconDefinition {
    return typeof icon === 'object' && icon !== null && 'iconName' in icon && 'prefix' in icon;
}

export function Icon({ icon, size = 'md', tone = 'default', iconColor, bgColor, className }: IconProps) {
    const color = iconColor ?? TONE_ICON_COLOR[tone];
    const fontSize = ICON_PX[size];

    return (
        <Wrapper $size={size} $tone={tone} $bgColor={bgColor} className={className}>
            {isFaIcon(icon) ? (
                <FontAwesomeIcon icon={icon} style={{ fontSize, color }} />
            ) : (
                React.createElement(icon, { sx: { fontSize, color } })
            )}
        </Wrapper>
    );
}

const shouldForwardWrapperProp = (prop: string) => !['$size', '$tone', '$bgColor'].includes(prop);

const Wrapper = styled('div', { shouldForwardProp: (p) => shouldForwardWrapperProp(p as string) })<{
    $size: IconSize;
    $tone: IconTone;
    $bgColor?: string;
}>(({ $size, $tone, $bgColor }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: CIRCLE_PX[$size],
    height: CIRCLE_PX[$size],
    borderRadius: '50%',
    backgroundColor: $bgColor ?? TONE_BG[$tone],
    border: $bgColor ? undefined : TONE_BORDER[$tone],
    flexShrink: 0,
}));
